import React, { useState } from 'react';
import { HelpCircle, Send, Check } from 'lucide-react';
import styles from './ClarificationCard.module.css';

export interface ClarificationQuestion {
  id: string;
  question: string;
  options?: string[];
  type: 'text' | 'select' | 'multi-select';
}

export interface ClarificationCardProps {
  questions: ClarificationQuestion[];
  onSubmit: (answers: Record<string, string>) => void;
  isSubmitted?: boolean;
}

export const ClarificationCard: React.FC<ClarificationCardProps> = ({
  questions,
  onSubmit,
  isSubmitted = false,
}) => {
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(isSubmitted);

  const handleAnswerChange = (questionId: string, value: string) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
  };

  const handleOptionSelect = (questionId: string, option: string) => {
    setAnswers(prev => {
      const current = prev[questionId] || '';
      // For multi-select, toggle the option
      const question = questions.find(q => q.id === questionId);
      if (question?.type === 'multi-select') {
        const selected = current ? current.split(', ') : [];
        const updated = selected.includes(option)
          ? selected.filter(s => s !== option)
          : [...selected, option];
        return { ...prev, [questionId]: updated.join(', ') };
      }
      return { ...prev, [questionId]: option };
    });
  };

  const handleSubmit = () => {
    if (Object.keys(answers).length === 0) return;
    setSubmitted(true);
    onSubmit(answers);
  };

  const allAnswered = questions.every(q => answers[q.id]?.trim());

  if (submitted) {
    return (
      <div className={styles.card}>
        <div className={styles.header}>
          <Check size={16} className={styles.headerIconDone} />
          <span className={styles.headerTitle}>Clarifications provided</span>
        </div>
        <div className={styles.submittedAnswers}>
          {questions.map(q => (
            <div key={q.id} className={styles.answeredItem}>
              <span className={styles.answeredQuestion}>{q.question}</span>
              <span className={styles.answeredValue}>{answers[q.id] || '—'}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <HelpCircle size={16} className={styles.headerIcon} />
        <span className={styles.headerTitle}>Clarification needed</span>
      </div>
      <div className={styles.questions}>
        {questions.map((q, idx) => (
          <div key={q.id} className={styles.questionBlock}>
            <label className={styles.questionLabel}>
              {q.question}
            </label>
            {q.options && q.options.length > 0 ? (
              <div className={styles.optionsGrid}>
                {q.options.map((option, optIdx) => {
                  const isSelected = q.type === 'multi-select'
                    ? (answers[q.id] || '').split(', ').includes(option)
                    : answers[q.id] === option;
                  return (
                    <button
                      key={optIdx}
                      className={`${styles.optionButton} ${isSelected ? styles.optionSelected : ''}`}
                      onClick={() => handleOptionSelect(q.id, option)}
                    >
                      <span className={styles.optionNumber}>{optIdx + 1}</span>
                      <span className={styles.optionText}>{option}</span>
                    </button>
                  );
                })}
              </div>
            ) : null}
            {/* Always show text input for freeform answers */}
            <div className={styles.inputRow}>
              <input
                type="text"
                className={styles.textInput}
                placeholder={q.options ? "Or type your answer..." : "Type your answer..."}
                value={answers[q.id] || ''}
                onChange={(e) => handleAnswerChange(q.id, e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && allAnswered) {
                    handleSubmit();
                  }
                }}
              />
            </div>
          </div>
        ))}
      </div>
      <div className={styles.footer}>
        <button
          className={styles.submitButton}
          onClick={handleSubmit}
          disabled={!allAnswered}
        >
          <Send size={14} />
          Submit Answers
        </button>
      </div>
    </div>
  );
};

/**
 * Parse assistant message content for clarification questions.
 * Format expected:
 * [CLARIFICATION]
 * Q: Question text here?
 * OPTIONS: Option 1 | Option 2 | Option 3
 * TYPE: select
 * ---
 * Q: Another question?
 * TYPE: text
 * [/CLARIFICATION]
 */
export function parseClarificationQuestions(content: string): {
  hasClarification: boolean;
  questions: ClarificationQuestion[];
  beforeText: string;
  afterText: string;
} {
  const startTag = '[CLARIFICATION]';
  const endTag = '[/CLARIFICATION]';
  
  const startIdx = content.indexOf(startTag);
  const endIdx = content.indexOf(endTag);
  
  if (startIdx === -1 || endIdx === -1 || endIdx <= startIdx) {
    return { hasClarification: false, questions: [], beforeText: content, afterText: '' };
  }
  
  const beforeText = content.slice(0, startIdx).trim();
  const afterText = content.slice(endIdx + endTag.length).trim();
  const block = content.slice(startIdx + startTag.length, endIdx).trim();
  
  const questionBlocks = block.split('---').filter(b => b.trim());
  const questions: ClarificationQuestion[] = questionBlocks.map((qBlock, idx) => {
    const lines = qBlock.trim().split('\n').map(l => l.trim());
    let question = '';
    let options: string[] = [];
    let type: 'text' | 'select' | 'multi-select' = 'text';
    
    for (const line of lines) {
      if (line.startsWith('Q:')) {
        question = line.slice(2).trim();
      } else if (line.startsWith('OPTIONS:')) {
        options = line.slice(8).split('|').map(o => o.trim()).filter(Boolean);
      } else if (line.startsWith('TYPE:')) {
        const t = line.slice(5).trim().toLowerCase();
        if (t === 'select' || t === 'multi-select') type = t;
      }
    }
    
    if (options.length > 0 && type === 'text') {
      type = 'select';
    }
    
    return { id: `cq-${idx}`, question, options: options.length > 0 ? options : undefined, type };
  }).filter(q => q.question);
  
  return { hasClarification: questions.length > 0, questions, beforeText, afterText };
}

export default ClarificationCard;
